"use client";
import { hasJeansWidthLengthSizes } from "@/app/product/[product]/components/ActionSection/util";
import ComingSoonModal from "@/app/product/[product]/components/ComingSoonModal/ComingSoonModal";
import JeansSizeSelector from "@/app/product/[product]/components/JeansSizeSelector";
import useJeansSizes from "@/app/product/[product]/hooks/useJeansSizes";
import useSizes from "@/app/product/[product]/hooks/useSizes";
import { convertProductCardToCartItem } from "@/lib/centra/convertToCartItem/convertToCartItem";
import { ProductCard } from "@/lib/centra/formatters/formatProductCards/formatProductCard";
import { useDPC } from "@/lib/depict/useDPC";
import useLocation from "@/lib/hooks/useLocation";
import { useCart } from "@/lib/hooks/useSelection";
import usePriceList from "@/lib/hooks/useSelection/cart/usePriceList";
import {
  convertPCToGtagItem,
  eventAddToCart,
} from "@/lib/utils/Analytics/Functions";
import { wishlist } from "@/lib/wishlist/hooks";
import { useSelection } from "@frend-digital/centra/client";
import clsx from "clsx";
import { useEffect, useState } from "react";
import Button from "../Button";
import { WishlistButton } from "../Wishlist/WishlistButton";
import styles from "./product-card.module.css";

type Size = {
  name: string;
  label: string;
  stock: boolean;
  itemId: string;
  fewLeft: boolean;
};

type Props = {
  sizes: Size[];
  product: ProductCard & { size?: { itemId: string } };
  actionClassName?: string;
  preselectSizeId?: string;
};

const ProductCardAction = ({
  sizes,
  product,
  actionClassName,
  preselectSizeId,
}: Props) => {
  const { data: selection } = useSelection();
  const { DPC } = useDPC();
  const isJeans = hasJeansWidthLengthSizes(sizes);
  const regularSizes = useSizes(sizes);
  const jeansHook = useJeansSizes(sizes);
  const [showActions, setShowActions] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const prices = usePriceList(product?.description?.price);
  const { currency } = useLocation();
  const cart = useCart();
  const itemId = product?.size?.itemId || product?.id;

  const newSizes = isJeans
    ? {
        sizes: jeansHook.jeansSizes,
        selectedSize: jeansHook.selection.selectedSize || undefined,
        selectSize: () => {},
      }
    : regularSizes;

  useEffect(() => {
    if (!preselectSizeId) return;
    const size = regularSizes?.sizes?.find(
      size => size.itemId === preselectSizeId,
    );

    if (size) {
      size.selectSize();
    }
  }, [preselectSizeId, sizes]);

  const getSelectedSizeText = () => {
    if (isJeans) {
      const { width, length } = jeansHook.selection;
      if (width && length) {
        return `W${width}/L${length}`;
      }
      if (width) {
        return `W${width}`;
      }
      return undefined;
    }
    return newSizes.selectedSize?.name;
  };

  const addToCart = () => {
    if (!newSizes?.selectedSize) return false;

    // Check if the selected size is out of stock
    if (!newSizes.selectedSize.stock) {
      setModalOpen(true);
      return;
    }

    if (newSizes?.selectedSize?.itemId)
      cart.addItem.mutate({
        itemId: newSizes?.selectedSize?.itemId,
        cartItem: convertProductCardToCartItem(
          product,
          newSizes?.selectedSize,
          prices,
        ),
      });

    DPC?.sendAddToCartEvent({
      product_id: newSizes?.selectedSize?.itemId.split("-").pop(),
    });

    if (eventAddToCart)
      eventAddToCart({
        $email:
          selection?.loggedIn?.email || selection?.selection?.address?.email,
        currency: currency || "",
        items: [
          convertPCToGtagItem(product, prices?.priceAsNumber, 1, {
            size_name: newSizes?.selectedSize.name,
          }),
        ],
        value: prices?.priceAsNumber,
        cart: {
          items: cart.items,
          total: cart.summary?.prices?.totalAsNumber ?? 0,
          addedItemId: newSizes?.selectedSize?.itemId,
        },
      });

    cart.modal.openModal();
  };

  if (!newSizes.sizes) return null;

  const buttonDisabled = isJeans
    ? !jeansHook.selection.selectedSize || !jeansHook.isSelectionComplete()
    : (!newSizes?.selectedSize?.stock ||
        !newSizes.selectedSize ||
        !newSizes?.selectedSize?.itemId) &&
      newSizes?.sizes?.length > 1;

  if (product?.comingSoon || !newSizes?.sizes?.some(size => size?.stock)) {
    return null;
  }

  return (
    <>
      <WishlistButton
        itemId={wishlist.createItemId({
          displayItemId: product.id!,
        })}
      />

      <ComingSoonModal
        item={newSizes.selectedSize}
        state={[modalOpen, setModalOpen]}
        product={
          {
            name: product?.core?.name,
            comingSoon: product?.comingSoon ? "1" : undefined,
          } as any
        }
        selectedSizeText={getSelectedSizeText()}
      />

      <div
        onClick={e => e.preventDefault()}
        tabIndex={-1}
        className={styles.root}
      >
        <Button
          color="white"
          rounded
          className={clsx(styles["pc-button"])}
          tabIndex={-1}
          onMouseEnter={() => setShowActions(true)}
          onMouseLeave={() => setShowActions(false)}
        >
          +
        </Button>
        <div
          className={clsx(
            styles.action,
            actionClassName,
            showActions && styles.show,
          )}
        >
          {(newSizes.sizes.length > 1 || isJeans) && (
            <div
              className={clsx(
                styles["actions-wrapper"],
                isJeans && styles["actions-wrapper_jeans"],
              )}
            >
              {isJeans ? (
                <div>
                  <JeansSizeSelector
                    jeansHook={jeansHook}
                    regularSizes={regularSizes}
                    comingSoon={false}
                    product={
                      {
                        name: product?.core?.name,
                        sizeguide: undefined,
                      } as any
                    }
                    showSizeGuide={false}
                    onNotifyClick={() => setModalOpen(true)}
                    className={styles["actionToggle"]}
                  />
                </div>
              ) : (
                newSizes.sizes.length > 1 &&
                regularSizes?.sizes
                  ?.filter(size => size.stock)
                  .map((size, i) => (
                    <button
                      key={i}
                      className={clsx(
                        styles["size-buttons"],
                        size.isSelected && styles.selected,
                      )}
                      disabled={!size.stock || size.isSelected}
                      onClick={size.selectSize}
                    >
                      <span
                        className={clsx(
                          !(!size.stock || size.isSelected) &&
                            "underline-hover",
                        )}
                      >
                        {size.label}
                      </span>
                    </button>
                  ))
              )}
            </div>
          )}

          <button
            className={clsx(styles.apply__to__cart)}
            disabled={buttonDisabled}
            style={
              newSizes?.sizes?.length === 1 ? { paddingBottom: "0.5rem" } : {}
            }
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              addToCart();
            }}
          >
            <span className={clsx(!buttonDisabled && "underline-hover")}>
              {!buttonDisabled
                ? "Add to cart"
                : isJeans
                  ? !jeansHook.selection.width
                    ? "Select Width"
                    : !jeansHook.selection.length
                      ? "Select Length"
                      : "Select Size"
                  : "Select Size"}
            </span>
          </button>
          {/* { !buttonDisabled && <span className={styles["wishlist-icon"]}>
            <Save size={newSizes?.selectedSize?.itemId} />
          </span>} */}
        </div>
      </div>
    </>
  );
};

export default ProductCardAction;
